import { theme } from 'antd';
import { EditIcon } from 'assets/images/svg';
import { DefaultCategoryIcon } from 'assets/images/svg/Categories';
import { defaultCategory } from 'config/defaultCategory';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { devicesActions } from 'reduxStore/devices/slice';
import { Avatar, Button, Flex, Input, Tooltip, Typography } from 'zui/Antd';

const { Text } = Typography;

const EditDrawerHeader = ({ deviceID, name, description, defaultCategoryID }) => {
	const canUpdateDevice = useManagePermittedRoles('updateDevice');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const formColRef = useRef(null);
	const {
		token: {
			paddingLG,
			colorTextLabel,
			fontSizeHeading3,
			fontSizeHeading5,
			colorText,
			fontWeightStrong,
			fontSizeSM,
			colorError
		}
	} = theme.useToken();

	// Get icon from defaultCategory object
	const CategoryIcon = defaultCategory[defaultCategoryID]?.icon || DefaultCategoryIcon;

	const [editable, setEditable] = useState(false);
	const [updatedName, setUpdatedName] = useState(name);
	const [errors, setErrors] = useState({ name: '' });

	// Reset when group changes
	useEffect(() => {
		setUpdatedName(name);
		setEditable(false);
		setErrors({ name: '' });
	}, [deviceID, name]);

	// Autofocus
	useEffect(() => {
		if (editable && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editable]);

	const handleFinish = useCallback(() => {
		const trimmedName = updatedName.trim();
		const newErrors = { name: '' };

		if (!trimmedName) {
			newErrors.name = 'Name is required';
		}

		setErrors(newErrors);

		if (newErrors.name) return;

		if (trimmedName !== name.trim()) {
			dispatch(devicesActions.updateDevice({ deviceID, data: { name: trimmedName } }));
		}

		setEditable(false);
	}, [updatedName, name, deviceID, dispatch]);

	// Save on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			const clickedOutside = formColRef.current && !formColRef.current.contains(event.target);
			if (editable && clickedOutside && canUpdateDevice) {
				handleFinish();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editable, handleFinish, canUpdateDevice]);

	return (
		<Flex gap={paddingLG}>
			{/* Icon */}
			<Avatar
				size={54}
				icon={<CategoryIcon color='white' style={{ width: 42, height: 42 }} />}
				style={{ backgroundColor: '#FFBB50', alignSelf: 'start', marginTop: 6 }}
				shape='square'
			/>

			{/* Name + UID */}
			<Flex vertical flex='1' ref={formColRef}>
				{editable ? (
					<>
						<Input
							ref={inputRef}
							name='groupName'
							style={{
								fontSize: fontSizeHeading3,
								fontWeight: fontWeightStrong,
								color: colorText,
								lineHeight: '32px',
								padding: 0
							}}
							value={updatedName}
							onPressEnter={handleFinish}
							onChange={(e) => setUpdatedName(e.target.value)}
							placeholder='Group name'
						/>
						{errors.name && (
							<Text
								style={{
									color: colorError,
									fontSize: fontSizeSM,
									fontWeight: fontWeightStrong
								}}
							>
								{errors.name}
							</Text>
						)}
					</>
				) : (
					<Text
						style={{
							fontSize: fontSizeHeading3,
							fontWeight: fontWeightStrong,
							lineHeight: '32px',
							color: colorText
						}}
					>
						{updatedName}
					</Text>
				)}

				<Text
					style={{
						fontSize: fontSizeHeading5,
						fontWeight: fontWeightStrong,
						color: colorTextLabel,
						lineHeight: '24px',
						marginBottom: 0
					}}
				>
					UID: {description}
				</Text>
			</Flex>

			{/* Edit Button */}
			<Tooltip title={!canUpdateDevice ? permissionDeniedMessage : ''}>
				<span>
					<Button
						onClick={() => canUpdateDevice && setEditable((prev) => !prev)}
						type='text'
						disabled={!canUpdateDevice}
						icon={<EditIcon color='currentColor' />}
						style={{ alignSelf: 'start' }}
					/>
				</span>
			</Tooltip>
		</Flex>
	);
};

export default EditDrawerHeader;
